import { MapPath } from "@/shared/state/visualization/schema/v1";
import { AsyncEvent } from "@/shared/synchronization";
import { MessagePayload, WorkerManager } from "@/shared/webworker";

import { CoordinateValidationError } from "./coordinateValidation";
import { getGeoMessagePaths } from "./geoTypes";
import { MapPathData } from "./MapPathData";
import {
  isInitializedEvent,
  isPathCoordinatesResponse,
  isPathDataCompleteResponse,
  MapDataCommand,
  SOURCE,
} from "./messaging";

export type MapLoadingState =
  | "loading-initial"
  | "loading-remaining"
  | "complete";

export class MapDataManager {
  #workerManager: WorkerManager;
  #pathDataMap: Map<string, MapPathData> = new Map();
  #onPathUpdated?: (pathId: string) => void;
  #onError?: (error: Error) => void;
  #onLoadingStateChange?: (state: MapLoadingState) => void;
  #abortController: AbortController;
  #initialized = new AsyncEvent();

  constructor({ onError }: { onError: (error: Error) => void }) {
    this.#onError = onError;
    this.#abortController = new AbortController();

    const worker = new Worker(
      new URL("./MapDataService.worker", import.meta.url),
      { type: "module", name: `MapDataService_${crypto.randomUUID()}` },
    );

    this.#workerManager = new WorkerManager({
      worker,
      onError: (err) => {
        this.#onLoadingStateChange?.("complete");
        this.#onError?.(err);
      },
      onMessage: this.#onMessage,
      signal: this.#abortController.signal,
    });

    // Send init command
    this.#workerManager.sendCommand({
      data: undefined,
      source: SOURCE,
      type: MapDataCommand.Init,
    });
  }

  /** Load path data from the worker */
  loadPathData(path: MapPath, signal?: AbortSignal): void {
    const { latMessagePath, lonMessagePath, latTopicData, lonTopicData } =
      getGeoMessagePaths(path);

    const latTopic = latTopicData.topic;
    const lonTopic = lonTopicData.topic;

    if (latTopic.id !== lonTopic.id) {
      this.#onError?.(
        new CoordinateValidationError(
          `Latitude and longitude must come from the same topic. Got topics "${latTopic.name}" and "${lonTopic.name}".`,
        ),
      );
      return;
    }

    const topicId = latTopic.id;
    const latMessagePathId = latTopicData.messagePath.id;
    const lonMessagePathId = lonTopicData.messagePath.id;

    // Create MapPathData for this path
    const pathData = new MapPathData();
    this.#pathDataMap.set(path.id, pathData);

    // We just created an empty MapPathData — we are now loading
    this.#onLoadingStateChange?.("loading-initial");

    // Send command to worker
    this.#workerManager.sendCommand({
      data: {
        pathId: path.id,
        topicId,
        latMessagePath,
        lonMessagePath,
        latMessagePathId,
        lonMessagePathId,
      },
      source: SOURCE,
      type: MapDataCommand.LoadPathData,
      signal,
    });
  }

  /** Get the MapPathData instance for a path */
  getPathData(pathId: string): MapPathData | undefined {
    return this.#pathDataMap.get(pathId);
  }

  /** Dispose a path and its associated topic reader */
  disposePath(path: MapPath): void {
    this.#pathDataMap.delete(path.id);

    // Both entries share the same topic
    const topicId = path.data[0].topic.id;
    this.#workerManager.sendCommand({
      data: { pathId: path.id, topicId },
      source: SOURCE,
      type: MapDataCommand.DisposePath,
    });
  }

  /** Dispose the manager and worker */
  dispose(): void {
    this.#abortController.abort();
    this.#workerManager.dispose();
    this.#pathDataMap.clear();
  }

  /** Register callback fired after each chunk is received */
  setOnPathUpdated(callback: (pathId: string) => void): void {
    this.#onPathUpdated = callback;
  }

  /** Returns a promise that resolves when the worker is initialized */
  waitForInitialized(): Promise<boolean> {
    return this.#initialized.wait();
  }

  /** Register loading state callback */
  setOnLoadingStateChange(callback: (state: MapLoadingState) => void): void {
    this.#onLoadingStateChange = callback;
  }

  #onMessage = (event: MessageEvent<MessagePayload>): void => {
    if (isPathCoordinatesResponse(event)) {
      const { pathId, latitudes, longitudes, logTimes } = event.data.data;
      const pathData = this.#pathDataMap.get(pathId);
      // The worker can still have chunks in flight for a path the user has
      // removed. There is nowhere to store them, so drop them silently rather
      // than announce a chunk that was never stored or reopen the loading
      // state for a panel that has finished.
      if (!pathData) {
        return;
      }

      // appendChunk rejects parallel arrays of differing length. The worker
      // pushes the three in lockstep so it cannot produce that today, but the
      // boundary's type guard reads only event.data.type and never the payload
      // shape, so the invariant holds by convention. Route a breach the same
      // way the worker's own errors are routed: announce "complete" first, or
      // the hold this load placed on the workspace timer is never released and
      // playback stays frozen with no error shown.
      try {
        pathData.appendChunk(latitudes, longitudes, logTimes);
      } catch (err) {
        this.#onLoadingStateChange?.("complete");
        this.#onError?.(
          err instanceof Error
            ? err
            : new Error("Failed to append map coordinate chunk", {
                cause: err,
              }),
        );
        return;
      }

      this.#onPathUpdated?.(pathId);
      this.#onLoadingStateChange?.("loading-remaining");
      return;
    }

    if (isPathDataCompleteResponse(event)) {
      const { pathId, totalCount } = event.data.data;
      this.#pathDataMap.get(pathId)?.markComplete(totalCount);

      const allComplete = [...this.#pathDataMap.values()].every(
        (p) => p.isComplete,
      );
      this.#onLoadingStateChange?.(
        allComplete ? "complete" : "loading-remaining",
      );
      return;
    }

    if (isInitializedEvent(event)) {
      this.#initialized.set();
      return;
    }
  };
}
